/**
 * Configuración de la app leída de variables de entorno de Vite.
 *
 * Mismo criterio que el resto de los frontends de la empresa: si falta una
 * variable **requerida**, falla ruidosamente al arrancar en vez de fallar
 * silenciosamente en cada request. Las opcionales tienen default explícito.
 *
 * ~~**Este sitio no tiene backend.**~~ Lo fue entre el 2026-09-18 y el
 * 2026-09-21: aquel día se retiró el de Ilumetria entero y salieron de aquí
 * `VITE_API_BASE_URL`, `VITE_COGNITO_*`, `VITE_IDENTITY_API_URL`,
 * `VITE_ATTENDEES_API_URL` y `VITE_REGISTRO_PIN`.
 *
 * **Vuelve a haber uno, y no es de Ilumetria**: `capital`, un producto de
 * FOCALIUS, en sus cuentas. De Ilumetria sólo salen estos bytes estáticos. Por
 * eso la variable nueva se llama `capitalApiUrl` y no `apiBaseUrl`: nombrarla
 * genérica escondería de quién es.
 *
 * Y se cumplió lo que decía este comentario: la variable se agregó **aquí y en
 * ningún otro sitio**, porque este módulo es el único que lee
 * `import.meta.env` y hay un test que lo vigila.
 */
interface AppConfig {
  appEnv: 'development' | 'staging' | 'production';
  /** URL pública canónica del sitio, usada para enlaces absolutos y metadatos. */
  siteUrl: string;
  contactEmail: string;
  privacyEmail: string;
  instagramUrl: string;
  facebookUrl: string;
  /**
   * Base de la API de `capital`, el backend del área de inversionistas.
   *
   * **Vacía = el área no está configurada en este entorno**, y la pantalla lo
   * dice en vez de llamar a `undefined/acceso/entrar`. Por eso no hay valor por
   * omisión: un default apuntando a un backend ajeno aparenta funcionar, que es
   * peor que no tener default. Hay un test que lo vigila.
   *
   * Lleva `API` en el nombre a propósito: así entra en el patrón que
   * `sinBackendsAjenos.test.ts` busca.
   */
  capitalApiUrl: string;
}

function readAppEnv(raw: string | undefined): AppConfig['appEnv'] {
  if (raw === 'staging' || raw === 'production') return raw;
  return 'development';
}

function stripTrailingSlash(value: string) {
  return value.replace(/\/+$/, '');
}

function buildConfig(): AppConfig {
  return {
    appEnv: readAppEnv(import.meta.env.VITE_APP_ENV),
    siteUrl: stripTrailingSlash(import.meta.env.VITE_SITE_URL ?? 'https://ilumetria.com'),
    contactEmail: import.meta.env.VITE_CONTACT_EMAIL || 'contacto@ilumetria.com',
    privacyEmail: import.meta.env.VITE_PRIVACY_EMAIL || 'contacto@ilumetria.com',
    instagramUrl: import.meta.env.VITE_INSTAGRAM_URL || 'https://www.instagram.com/ilumetriamx',
    facebookUrl: import.meta.env.VITE_FACEBOOK_URL || 'https://www.facebook.com/share/185jKNqNrC/',
    capitalApiUrl: stripTrailingSlash(import.meta.env.VITE_CAPITAL_API_URL ?? ''),
  };
}

export const config: AppConfig = buildConfig();
